import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { logout } from "@/store/slices/authSlice";
import Notificationicon from "@styles/all-images/icons/notification-outline-badged.svg";
import UserDropdown from "@styles/all-images/icons/user-dropdown.svg";

const Header = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  // Set "Operational User" as the initial selected value
  const [selectedValue, setSelectedValue] = useState("Operational User");

  // Mapping of options to background colors
  const backgroundColors: Record<string, string> = {
    "Primary Accountant": "tw-bg-[#D6F4FD]",
    "Commodity Trader 01": "tw-bg-[#FFE1D3]",
    "Operational User": "tw-bg-[#EDDEFF]",
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (value: string) => {
    setSelectedValue(value);
    setIsOpen(false); // Close the dropdown after selection
  };

  const [isRightOpen, setIsRightOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(() => {
    // Set initial value based on current route
    if (location.pathname === "/my-account") return "My Account";
    if (location.pathname === "/change-password") return "Change Password";
    return "Select an option";
  });

  // Update selection when route changes
  useEffect(() => {
    if (location.pathname === "/my-account") {
      setSelectedOption("My Account");
    } else if (location.pathname === "/change-password") {
      setSelectedOption("Change Password");
    }
  }, [location.pathname]);

  const righttoggleDropdown = () => {
    setIsRightOpen(!isRightOpen);
  };

  const handleLogout = () => {
    dispatch(logout()); // This will clear auth state and localStorage
    navigate("/"); // Redirect to home/login page
  };

  const righthandleOptionClick = (value: string) => {
    setSelectedOption(value);
    setIsRightOpen(false);

    // Handle logout when that option is selected
    if (value === "Logout") {
      handleLogout();
    }
  };

  // Get the dynamic background class
  const headerBgClass = backgroundColors[selectedValue] || "tw-bg-purple-100";

  const handleMyAccount = () => {
    setIsRightOpen(false);
    setSelectedOption("My Account");
    navigate("/my-account");
  };

  return (
    <header
      className={`${headerBgClass} tw-h-[80px] tw-top-0 tw-fixed tw-w-full tw-z-20 tw-px-8 tw-py-4 tw-flex tw-items-center tw-justify-between tw-border-b tw-border-gray-200`}
    >
      <div className="tw-cursor-pointer" onClick={() => navigate("/dashboard")}>
        <img
          src="/logo/Logo.svg"
          alt="Logo"
          className="tw-mx-auto tw-max-w-[139px] tw-max-h-[32px]"
        />
      </div>
      <div>
        <div className="tw-relative">
          <button
            className="tw-flex tw-items-center tw-px-4 tw-py-2 tw-bg-transparent tw-text-black tw-rounded-md"
            onClick={toggleDropdown}
          >
            <span className="tw-mr-3 tw-font-semibold tw-text-[#535862]">
              {selectedValue}
            </span>
            <svg
              className={`tw-w-4 tw-h-4 tw-transform ${isOpen ? "tw-rotate-180" : "tw-rotate-0"}`}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {isOpen && (
            <div className="tw-absolute tw-right-0 tw-w-56 tw-bg-white tw-rounded-xl tw-shadow-lg tw-border-gray-200 tw-border tw-p-3 tw-z-[100]">
              <ul className="tw-py-1">
                <li
                  onClick={() => handleOptionClick("Primary Accountant")}
                  className="tw-px-4 tw-py-3 tw-hover:bg-gray-100 tw-cursor-pointer"
                >
                  Primary Accountant
                </li>
                <li
                  onClick={() => handleOptionClick("Commodity Trader 01")}
                  className="tw-px-4 tw-py-3 tw-hover:bg-gray-100 tw-cursor-pointer"
                >
                  Commodity Trader 01
                </li>
                <li
                  onClick={() => handleOptionClick("Operational User")}
                  className="tw-px-4 tw-py-3 tw-hover:bg-gray-100 tw-cursor-pointer"
                >
                  Operational User
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
      <div className="tw-flex tw-items-center">
        <div className="tw-mr-5 tw-cursor-pointer">
          <img src={Notificationicon} alt="" />
        </div>
        <div className="tw-relative">
          <button
            className="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-2 tw-bg-transparent tw-border tw-border-[#CBD5E1] tw-text-black tw-rounded-lg tw-w-[250px]"
            onClick={righttoggleDropdown}
          >
            <div className="tw-flex tw-items-center">
              <img src={UserDropdown} alt="" />
              <span className="tw-mr-3 tw-ml-3 tw-font-semibold tw-text-[#535862]">
                {selectedOption}
              </span>
            </div>
            <div>
              <svg
                className={`tw-w-4 tw-h-4 tw-transform ${
                  isRightOpen ? "tw-rotate-180" : "tw-rotate-0"
                }`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </button>

          {isRightOpen && (
            <div className="tw-absolute tw-right-0 tw-w-56 tw-bg-white tw-rounded-xl tw-shadow-lg tw-border-gray-200 tw-mt-2 tw-p-3">
              <ul className="tw-py-1">
                <button
                  className="tw-w-full tw-text-left tw-px-4 tw-py-2 tw-hover:bg-gray-100 tw-cursor-pointer tw-border-0 tw-bg-transparent"
                  onClick={handleMyAccount}
                >
                  My Account
                </button>
                <button
                  className="tw-w-full tw-text-left tw-px-4 tw-py-2 tw-hover:bg-gray-100 tw-cursor-pointer tw-border-0 tw-bg-transparent"
                  onClick={() => navigate("/change-password")}
                >
                  Change Password
                </button>
                <button
                  className="tw-w-full tw-text-left tw-px-4 tw-py-2 tw-hover:bg-gray-100 tw-cursor-pointer tw-border-0 tw-bg-transparent"
                  onClick={() => righthandleOptionClick("Logout")}
                >
                  Logout
                </button>
              </ul>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
